import React from 'react';
import { 
  Printer, Copy, BookOpen, Camera, Scan,
  Sticker, CreditCard, FileText, Mail, Gift, Frame,
  HardDrive, Zap, Laptop, Gauge, FolderOpen, Cpu, MapPin, Stamp, ArrowRight
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { services } from '../utils/mockData';

const iconMap = {
  Printer, Copy, BookOpen, Camera, Scan,
  Sticker, CreditCard, FileText, Mail, Gift, Frame,
  HardDrive, Zap, Laptop, Gauge, FolderOpen, Cpu, MapPin, Stamp
};

const ServiceCard = ({ service }) => {
  const IconComponent = iconMap[service.icon];
  
  const scrollToSection = () => {
    if (service.linkTo) {
      const element = document.getElementById(service.linkTo);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };
  
  if (service.featured && service.linkTo) {
    return (
      <Card className="group hover:shadow-xl transition-all duration-300 border-2 border-[#1e3a8a]/20 hover:border-[#1e3a8a] hover:-translate-y-1 bg-gradient-to-br from-blue-50/50 to-white">
        <CardContent className="p-6">
          <div className="w-14 h-14 bg-gradient-to-br from-[#1e3a8a] to-[#3b82f6] rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-all">
            <IconComponent className="w-7 h-7 text-white" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-[#1e3a8a] transition-colors">
            {service.title}
          </h3>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            {service.description}
          </p>
          <Button
            onClick={scrollToSection}
            variant="outline"
            size="sm"
            className="w-full border-[#1e3a8a] text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white transition-all">
            Conheça os carimbos
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="group hover:shadow-xl transition-all duration-300 border-gray-200 hover:border-[#1e3a8a]/30 hover:-translate-y-1">
      <CardContent className="p-6">
        <div className="w-14 h-14 bg-gradient-to-br from-[#1e3a8a]/10 to-[#3b82f6]/10 rounded-lg flex items-center justify-center mb-4 group-hover:from-[#1e3a8a]/20 group-hover:to-[#3b82f6]/20 transition-all">
          <IconComponent className="w-7 h-7 text-[#1e3a8a]" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-[#1e3a8a] transition-colors">
          {service.title}
        </h3>
        <p className="text-gray-600 text-sm leading-relaxed">
          {service.description}
        </p>
      </CardContent>
    </Card>
  );
};

const Services = () => {
  return (
    <section id="servicos" className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4 px-4 py-2 bg-[#1e3a8a]/10 rounded-full">
            <p className="text-[#1e3a8a] text-sm font-semibold">NOSSOS SERVIÇOS</p>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Todos os Serviços em um Só Lugar
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Gráfica completa + Assistência técnica especializada. Economize tempo e dinheiro!
          </p>
        </div>

        {/* Gráfica Rápida */}
        <div className="mb-16">
          <div className="flex items-center mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-[#1e3a8a] to-[#3b82f6] rounded-lg flex items-center justify-center mr-4">
              <Printer className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900">Gráfica Rápida</h3>
              <p className="text-gray-600">Impressão profissional. Preços que cabem no seu bolso!</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.grafica.map(service => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </div>
        </div>

        {/* Personalizados */}
        <div className="mb-16">
          <div className="flex items-center mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-[#f97316] to-[#fb923c] rounded-lg flex items-center justify-center mr-4">
              <Gift className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900">Personalizados</h3>
              <p className="text-gray-600">Dê vida à sua marca! Qualidade que impressiona.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.personalizados.map(service => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </div>
        </div>

        {/* Assistência Técnica */}
        <div>
          <div className="flex items-center mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-[#059669] to-[#10b981] rounded-lg flex items-center justify-center mr-4">
              <Laptop className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900">Assistência Técnica</h3>
              <p className="text-gray-600">Conserto rápido e garantido. Diagnóstico grátis!</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.assistencia.map(service => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
